import { call, put, select, spawn, takeLeading } from "redux-saga/effects";

import { CharacterBaseStats, InstalledUpgradesData, PartTypes } from "types";

import { getInvalidUpgradeParts, getUpgradeData } from "libs/item";
import { getBaseStatData, getPartData } from "libs/stats";
import {
  addEventLog,
  installUpgrade,
  moveUpgradeFromStorage,
  moveUpgradeToStorage,
  setInstalledUpgrade,
  setUninstalledUpgrade,
  showMessage,
  storeUpgrade,
  uninstallUpgrade,
  unstoreUpgrade,
} from "redux/actions";
import {
  getInstalledUpgrades,
  getTotalBaseStats,
  getUpgrades,
} from "redux/selectors/characterSelectors";
import { adjustHealthSaga } from "./character";
import { uiAnimation } from "./game";

export function* installUpgradeSaga({ payload }: { payload: string }) {
  const unlockedUpgrades: string[] = yield select(getUpgrades);
  const installedUpgrades: InstalledUpgradesData = yield select(
    getInstalledUpgrades
  );
  const totalBaseStats: CharacterBaseStats = yield select(getTotalBaseStats);

  const upgrade = getUpgradeData(payload);
  if (!upgrade) {
    return;
  }
  const partInfo = getPartData(upgrade.part);

  // Make sure the player actually has this upgrade
  if (!unlockedUpgrades.includes(payload)) {
    return;
  }

  try {
    const { requirements } = upgrade;
    for (const key in requirements) {
      const baseStat = key as keyof CharacterBaseStats;
      const reqValue = requirements[baseStat] as number;
      const baseStatValue = totalBaseStats[baseStat];
      const baseStatInfo = getBaseStatData(baseStat);

      if (baseStatValue < reqValue) {
        throw new Error(
          `Requires ${baseStatInfo.name} ${reqValue} or higher to install.`
        );
      }
    }

    // Check if the same upgrade is already installed
    const existingUpgrade = installedUpgrades[upgrade.part];
    if (existingUpgrade) {
      if (existingUpgrade === payload) {
        throw new Error(`You already have this upgrade installed`);
      }
    }

    // Install new upgrade
    yield put(
      setInstalledUpgrade({
        part: upgrade.part,
        slug: payload,
      })
    );

    // Adjust health to new maxHealth
    yield call(adjustHealthSaga);

    // Show install animation
    yield spawn(uiAnimation, `${upgrade.part}Install`);

    yield put(showMessage(`Installed new ${partInfo.name}: ${upgrade.name}`));

    yield put(
      addEventLog({
        event: "install_upgrade",
        eventParams: { upgrade: payload, part: upgrade.part },
      })
    );
  } catch (error: any) {
    yield put(showMessage(error.message));
  }
}

export function* uninstallUpgradeSaga({
  payload: part,
}: {
  payload: PartTypes;
}) {
  const installedUpgrades: InstalledUpgradesData = yield select(
    getInstalledUpgrades
  );
  const existingUpgrade = installedUpgrades[part];
  if (!existingUpgrade) {
    return;
  }
  const upgrade = getUpgradeData(existingUpgrade);
  const partInfo = getPartData(part);

  try {
    // Uninstall existing upgrade
    yield put(setUninstalledUpgrade(part));

    // Adjust health to new maxHealth
    yield call(adjustHealthSaga);

    yield put(showMessage(`Uninstalled ${partInfo.name}: ${upgrade.name}`));
  } catch (error: any) {
    yield put(showMessage(error.message));
  }
}

export function* storeUpgradeSaga({ payload }: { payload: string }) {
  const upgrade = getUpgradeData(payload);

  yield put(moveUpgradeToStorage({ slug: payload }));

  yield put(showMessage(`Moved ${upgrade.name} to storage`));
}

export function* unstoreUpgradeSaga({ payload }: { payload: string }) {
  const upgrade = getUpgradeData(payload);

  yield put(moveUpgradeFromStorage({ slug: payload }));

  yield put(showMessage(`Moved ${upgrade.name} out of storage`));
}

export function* adjustUpgradesSaga() {
  const installedUpgrades: InstalledUpgradesData = yield select(
    getInstalledUpgrades
  );
  const totalBaseStats: CharacterBaseStats = yield select(getTotalBaseStats);

  const invalidParts = getInvalidUpgradeParts(
    installedUpgrades,
    totalBaseStats
  );

  for (const partKey of invalidParts) {
    const part = partKey as PartTypes;

    yield call(uninstallUpgradeSaga, { payload: part });
  }

  if (invalidParts.length > 0) {
    yield put(
      showMessage(`Certain upgrades were uninstalled due to insufficient stats`)
    );
  }
}

export default function* itemSagas() {
  yield takeLeading(installUpgrade, installUpgradeSaga);
  yield takeLeading(uninstallUpgrade, uninstallUpgradeSaga);
  yield takeLeading(storeUpgrade, storeUpgradeSaga);
  yield takeLeading(unstoreUpgrade, unstoreUpgradeSaga);
}
